import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { 
  User, 
  Mail, 
  Calendar, 
  Clock,
  Building2,
  Shield,
  Activity,
  Edit,
  Settings,
  Phone,
  MapPin
} from "lucide-react";

const UserProfileModal = ({ 
  open, 
  onClose, 
  member,
  userRoles = [],
  roles = [],
  subsidiaries = [],
  onEdit,
  onManageAccess
}) => {
  if (!member) return null;

  // Get member's roles - use member.roles from backend if available
  const memberRoles = member.roles || userRoles.filter(ur => ur.user_id === member.id);
  const roleDetails = memberRoles
    .map(role => ({
      role: role.role_name ? { name: role.role_name, id: role.role_id } : roles.find(r => r.id === role.role_id),
      subsidiary: role.subsidiary_id ? subsidiaries.find(s => s.id === role.subsidiary_id) : null
    }))
    .filter(item => item.role);
  
  // Get unique subsidiaries - use member.subsidiaries if available
  const uniqueSubsidiaries = member.subsidiaries 
    ? member.subsidiaries.map(s => ({ id: s.subsidiary_id, name: s.subsidiary_name }))
    : [...new Set(
        memberRoles
          .map(ur => subsidiaries.find(s => s.id === ur.subsidiary_id))
          .filter(Boolean)
      )];

  // Calculate account age
  const accountAge = () => {
    const created = new Date(member.created_at);
    const now = new Date();
    const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`;
    return `${Math.floor(diffDays / 365)} years`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="text-xl font-bold">{member.full_name || "N/A"}</div>
              <div className="text-sm text-gray-500 font-normal">
                User ID: #{member.id?.slice(0, 12) || "N/A"}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status & Quick Actions */}
          <div className="flex items-center justify-between pb-4 border-b">
            <Badge 
              variant={member.is_active ? "default" : "secondary"}
              className={member.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}
            >
              {member.is_active ? "✅ Active Account" : "Inactive Account"}
            </Badge>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  onEdit && onEdit(member);
                  onClose();
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  onManageAccess && onManageAccess(member);
                  onClose();
                }}
              >
                <Settings className="h-4 w-4 mr-2" />
                Manage Access
              </Button>
            </div>
          </div>

          {/* Personal Information */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <User className="h-4 w-4" />
              PERSONAL INFORMATION
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500">Full Name</label>
                <p className="text-sm font-medium text-gray-900">{member.full_name || "N/A"}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Email Address</label>
                <div className="flex items-center gap-2">
                  <Mail className="h-3 w-3 text-gray-400" />
                  <p className="text-sm font-medium text-gray-900">{member.email}</p>
                </div>
              </div>
              {member.phone && (
                <div>
                  <label className="text-xs text-gray-500">Phone Number</label>
                  <div className="flex items-center gap-2">
                    <Phone className="h-3 w-3 text-gray-400" />
                    <p className="text-sm font-medium text-gray-900">{member.phone}</p>
                  </div>
                </div>
              )}
              {member.location && (
                <div>
                  <label className="text-xs text-gray-500">Location</label>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3 w-3 text-gray-400" />
                    <p className="text-sm font-medium text-gray-900">{member.location}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Account Information */}
          <div className="border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              ACCOUNT INFORMATION
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500">Join Date</label>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(member.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Account Age</label>
                <p className="text-sm font-medium text-gray-900">{accountAge()}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500">Last Activity</label>
                <div className="flex items-center gap-2">
                  <Clock className="h-3 w-3 text-gray-400" />
                  <p className="text-sm font-medium text-gray-900">
                    {member.updated_at 
                      ? new Date(member.updated_at).toLocaleDateString() 
                      : "N/A"}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500">Account Status</label>
                <p className="text-sm font-medium text-gray-900">
                  {member.is_active ? "Active" : "Inactive"}
                </p>
              </div>
            </div>
          </div>

          {/* Subsidiary Assignments */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              SUBSIDIARY ASSIGNMENTS ({uniqueSubsidiaries.length})
            </h3>
            {uniqueSubsidiaries.length > 0 ? (
              <div className="space-y-3">
                {uniqueSubsidiaries.map((sub) => (
                  <div key={sub.id} className="bg-white rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm">{sub.name}</p>
                        {sub.location && (
                          <p className="text-xs text-gray-500 mt-1">
                            <MapPin className="h-3 w-3 inline mr-1" />
                            {sub.location}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {sub.team_count || 0} members
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No subsidiary assignments</p>
            )}
          </div>

          {/* Roles & Permissions */}
          <div className="border rounded-lg p-4 bg-purple-50">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              ROLES & PERMISSIONS ({roleDetails.length})
            </h3>
            {roleDetails.length > 0 ? (
              <div className="space-y-3">
                {roleDetails.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-sm">{item.role.name}</p>
                        {item.subsidiary && (
                          <p className="text-xs text-gray-500 mt-1">
                            <Building2 className="h-3 w-3 inline mr-1" />
                            {item.subsidiary.name}
                          </p>
                        )}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {item.role.is_system_role ? "System Role" : "Custom Role"}
                      </Badge>
                    </div>
                    {item.role.description && (
                      <p className="text-xs text-gray-600 mt-2">{item.role.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No roles assigned</p>
            )}
          </div>

          {/* Activity History */}
          <div className="border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              ACTIVITY HISTORY
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="font-medium">Account created</p>
                  <p className="text-xs text-gray-500">
                    {new Date(member.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              {member.updated_at && member.updated_at !== member.created_at && (
                <div className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <p className="font-medium">Last activity</p>
                    <p className="text-xs text-gray-500">
                      {new Date(member.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
              {roleDetails.length > 0 && (
                <div className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <p className="font-medium">Roles assigned</p>
                    <p className="text-xs text-gray-500">
                      {roleDetails.length} role{roleDetails.length !== 1 ? 's' : ''} currently active
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileModal;
